import { useState, useEffect } from "react";
import { 
  Send, 
  Plus, 
  Trash2, 
  Play,
  Pause,
  Edit,
  MoreVertical,
  Calendar,
  Mail,
  Globe,
  CheckCircle,
  Clock,
  AlertCircle,
  Users
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  getCampaigns, 
  createCampaign, 
  deleteCampaign, 
  launchCampaign,
  getTemplates,
  getWebsites,
  getContactLists,
  getContactListWebsites
} from "../lib/api";
import { motion } from "framer-motion";

const StatusBadge = ({ status }) => {
  const config = {
    draft: { icon: Edit, class: "bg-accent text-muted-foreground", label: "Draft" },
    active: { icon: Play, class: "badge-success", label: "Active" },
    paused: { icon: Pause, class: "badge-warning", label: "Paused" },
    completed: { icon: CheckCircle, class: "badge-success", label: "Completed" }
  };
  
  const { icon: Icon, class: className, label } = config[status] || config.draft;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectionMode, setSelectionMode] = useState("list"); // "list" or "individual"
  const [selectedListId, setSelectedListId] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    template_id: "",
    website_ids: [],
    reminder_count: 5,
    reminder_interval_days: 3
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, templatesRes, websitesRes, listsRes] = await Promise.all([
        getCampaigns(),
        getTemplates(),
        getWebsites({ has_email: true }),
        getContactLists()
      ]);
      setCampaigns(campaignsRes.data);
      setTemplates(templatesRes.data);
      setWebsites(websitesRes.data);
      setContactLists(listsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectList = async (listId) => {
    setSelectedListId(listId);
    if (listId) {
      try {
        const response = await getContactListWebsites(listId);
        const websiteIds = response.data.map(w => w.id);
        setFormData(f => ({ ...f, website_ids: websiteIds }));
      } catch (error) {
        toast.error("Failed to load list websites");
      }
    } else {
      setFormData(f => ({ ...f, website_ids: [] }));
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }

    setCreating(true);
    try {
      await createCampaign(formData);
      toast.success("Campaign created");
      setShowCreateDialog(false);
      setFormData({
        name: "",
        description: "",
        template_id: "",
        website_ids: [],
        reminder_count: 5,
        reminder_interval_days: 3
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create campaign");
    } finally {
      setCreating(false);
    }
  };

  const handleLaunch = async (id) => {
    try {
      const response = await launchCampaign(id);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to launch campaign");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCampaign(id);
      toast.success("Campaign deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const toggleWebsite = (websiteId) => {
    setFormData(prev => ({
      ...prev,
      website_ids: prev.website_ids.includes(websiteId)
        ? prev.website_ids.filter(id => id !== websiteId)
        : [...prev.website_ids, websiteId]
    }));
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-muted-foreground mt-1">
            Manage your outreach campaigns with automated follow-ups
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-campaign-dialog">
            <DialogHeader>
              <DialogTitle>Create Campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Tech Blog Outreach Q1"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  data-testid="campaign-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of your campaign goals..."
                  value={formData.description}
                  onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                  data-testid="campaign-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Template</Label>
                <Select 
                  value={formData.template_id}
                  onValueChange={(v) => setFormData(f => ({ ...f, template_id: v }))}
                >
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {templates.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No templates yet. Create one in the Templates section.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="reminders">Follow-up Reminders</Label>
                  <Select 
                    value={formData.reminder_count.toString()}
                    onValueChange={(v) => setFormData(f => ({ ...f, reminder_count: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="reminder-count-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} reminders</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interval">Interval (days)</Label>
                  <Select 
                    value={formData.reminder_interval_days.toString()}
                    onValueChange={(v) => setFormData(f => ({ ...f, reminder_interval_days: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2, 3, 5, 7, 10, 14].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} days</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Target Selection - Contact Lists or Individual */}
              <div className="space-y-3">
                <Label>Target Contacts</Label>
                <Tabs value={selectionMode} onValueChange={setSelectionMode}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="list" data-testid="tab-list">
                      <Users className="w-4 h-4 mr-2" />
                      Contact List
                    </TabsTrigger>
                    <TabsTrigger value="individual" data-testid="tab-individual">
                      <Globe className="w-4 h-4 mr-2" />
                      Individual
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="list" className="mt-3">
                    <Select value={selectedListId} onValueChange={handleSelectList}>
                      <SelectTrigger data-testid="contact-list-select">
                        <SelectValue placeholder="Select a contact list" />
                      </SelectTrigger>
                      <SelectContent>
                        {contactLists.map(list => (
                          <SelectItem key={list.id} value={list.id}>
                            {list.name} ({list.total_contacts} contacts)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {contactLists.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        No contact lists yet. Create one from the Websites page.
                      </p>
                    )}
                    {selectedListId && (
                      <p className="text-xs text-green-500 mt-2">
                        ✓ {formData.website_ids.length} contacts loaded from list
                      </p>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="individual" className="mt-3">
                    <div className="border border-border rounded-md max-h-48 overflow-y-auto">
                      {websites.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground text-center">
                          No websites with emails found. Add websites first.
                        </p>
                      ) : (
                        websites.map(w => (
                          <label 
                            key={w.id}
                            className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer border-b border-border last:border-0"
                          >
                            <input
                              type="checkbox"
                              checked={formData.website_ids.includes(w.id)}
                              onChange={() => toggleWebsite(w.id)}
                              className="rounded border-border"
                              data-testid={`website-checkbox-${w.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{w.domain}</p>
                              <p className="text-xs text-muted-foreground truncate">{w.contact_email}</p>
                            </div>
                            <span className="score-badge high text-xs">{w.quality_score ?? "N/A"}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
                <p className="text-xs text-muted-foreground">
                  {formData.website_ids.length} contacts selected
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={creating}
                data-testid="save-campaign-btn"
              >
                {creating ? "Creating..." : "Create Campaign"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campaigns Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="loading-skeleton">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton h-48 rounded-md" />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="empty-state" data-testid="empty-state">
            <Send className="w-16 h-16" strokeWidth={1} />
            <h3 className="text-lg font-semibold mt-4">No campaigns yet</h3>
            <p className="text-sm max-w-sm mt-2">
              Create your first outreach campaign to start connecting with websites.
            </p>
            <Button 
              className="mt-4" 
              onClick={() => setShowCreateDialog(true)}
              data-testid="empty-create-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
          {campaigns.map((campaign, index) => (
            <motion.div
              key={campaign.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="bg-card border-border hover:border-primary/30 transition-colors h-full" data-testid={`campaign-card-${campaign.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{campaign.name}</CardTitle>
                      <StatusBadge status={campaign.status} />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`campaign-menu-${campaign.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {campaign.status === "draft" && (
                          <DropdownMenuItem onClick={() => handleLaunch(campaign.id)} data-testid={`launch-${campaign.id}`}>
                            <Play className="w-4 h-4 mr-2" />
                            Launch Campaign
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleDelete(campaign.id)}
                          className="text-destructive"
                          data-testid={`delete-${campaign.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {campaign.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {campaign.description}
                    </p>
                  )}
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <div className="text-center p-2 bg-accent/50 rounded">
                      <p className="text-lg font-bold">{campaign.stats?.sent || 0}</p>
                      <p className="text-xs text-muted-foreground">Sent</p>
                    </div>
                    <div className="text-center p-2 bg-accent/50 rounded">
                      <p className="text-lg font-bold">{campaign.stats?.opened || 0}</p>
                      <p className="text-xs text-muted-foreground">Opened</p>
                    </div>
                    <div className="text-center p-2 bg-accent/50 rounded">
                      <p className="text-lg font-bold text-green-500">{campaign.stats?.replied || 0}</p>
                      <p className="text-xs text-muted-foreground">Replied</p>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border">
                    <span className="flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {campaign.website_ids?.length || 0} sites
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {campaign.reminder_count} reminders
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
